// services/frontend/src/hooks/useZoomPan.ts

import { useState, useCallback, useEffect, useRef } from 'react';
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { Photo } from '../types/photo';
import type { ZoomPanContextValue } from '@/contexts/ZoomPanContext';
import logger from '../utils/logger';
import { getVariantUrl, selectBestVariant } from '@/components/photos/AdaptivePhoto';

export type ZoomLevel = 'fit' | number;

/**
 * Preset zoom levels for dropdown UI
 * Note: ZoomLevel type allows any number - users can type custom values
 * These are just common presets for quick selection
 */
export const ZOOM_LEVELS: ReadonlyArray<'fit' | number> = [
  'fit',
  10, 25, 50, 75, 100, 150, 200, 300
] as const;

/**
 * Calculate display dimensions accounting for device pixel ratio
 *
 * At 100% zoom on 2x display: 4000px source → 2000 CSS pixels
 * This ensures 1 source pixel = 1 physical pixel (true pixel-perfect display)
 *
 * @param sourceWidth - Natural image width in pixels
 * @param sourceHeight - Natural image height in pixels
 * @param zoomPercent - Zoom level as percentage (e.g., 100 for 100%)
 * @param containerWidth - Container width for Fit mode calculation
 * @param containerHeight - Container height for Fit mode calculation
 * @returns CSS dimensions { width, height } in pixels
 */
export function calculateDisplayDimensions(
  sourceWidth: number,
  sourceHeight: number,
  zoomPercent: number,
  containerWidth?: number,
  containerHeight?: number
): { width: number; height: number } {
  // Fit mode: Scale to fill container while maintaining aspect ratio
  if (containerWidth && containerHeight) {
    const widthRatio = containerWidth / sourceWidth;
    const heightRatio = containerHeight / sourceHeight;
    const scale = Math.min(widthRatio, heightRatio);

    return {
      width: sourceWidth * scale,
      height: sourceHeight * scale
    };
  }

  // Percentage zoom: Account for device pixel ratio
  const dpr = window.devicePixelRatio || 1;
  const cssWidth = (sourceWidth * zoomPercent / 100) / dpr;
  const cssHeight = (sourceHeight * zoomPercent / 100) / dpr;

  return { width: cssWidth, height: cssHeight };
}

/**
 * Select image resolution based on target display size
 *
 * Feature 646: Use actual pixel dimensions instead of zoom percentage
 * - If targetWidth >= medium width (1920px): Use 'full' for quality
 * - Otherwise: Use 'medium' to save bandwidth
 *
 * Falls back to zoom-based selection if targetWidth not provided
 *
 * @param zoomLevel - Current zoom level (fallback)
 * @param targetWidth - Target display width in CSS pixels
 * @returns 'medium' or 'full' resolution variant
 */
export function selectImageResolution(
  zoomLevel: ZoomLevel,
  targetWidth?: number
): 'medium' | 'full' {
  // If target width provided, use that for selection
  if (targetWidth !== undefined) {
    return targetWidth >= 1920 ? 'full' : 'medium';
  }

  // Fallback to zoom-based selection
  if (zoomLevel === 'fit') return 'medium';
  if (typeof zoomLevel === 'number' && zoomLevel < 60) return 'medium';
  return 'full';
}

interface ZoomPanState {
  zoomLevel: ZoomLevel;
  scale: number;
  panX: number;
  panY: number;
  isPanning: boolean;
}

interface UseZoomPanOptions {
  photo: Photo;
  onImageSourceChange?: (url: string) => void;
  sharedState?: ZoomPanContextValue;
  targetWidth?: number;
  targetHeight?: number;
}

interface UseZoomPanReturn {
  zoomLevel: ZoomLevel;
  scale: number;
  isPanning: boolean;
  isLoading: boolean;
  imageSource: string;
  viewportWarning: string | null;
  transformRef: React.RefObject<ReactZoomPanPinchRef>;
  containerRef: React.RefObject<HTMLDivElement>;
  containerSize: { width: number; height: number };
  setZoomLevel: (level: ZoomLevel) => void;
  reset: () => void;
  getCursorStyle: () => string;
  handleTransformed: (ref: ReactZoomPanPinchRef) => void;
  handleImageLoadForScale: (img: HTMLImageElement) => void;
}

/**
 * Custom hook for managing zoom and pan functionality using react-zoom-pan-pinch.
 *
 * Features:
 * - Zoom levels: fit-to-screen, 50%, 100%, 200%
 * - Dynamic image source switching (medium variant for <100% zoom, full for >=100%)
 * - Trackpad pinch-to-zoom (handled by library)
 * - Click + drag panning (handled by library)
 * - Double-click toggle zoom
 * - Zoom-to-cursor behavior
 */
export function useZoomPan({
  photo,
  onImageSourceChange,
  sharedState,
  targetWidth,
  targetHeight,
}: UseZoomPanOptions): UseZoomPanReturn {
  const [localState, setLocalState] = useState<ZoomPanState>({
    zoomLevel: 'fit',
    scale: 1,
    panX: 0,
    panY: 0,
    isPanning: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [viewportWarning, setViewportWarning] = useState<string | null>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [currentScale, setCurrentScale] = useState<number>(1);

  // Track the initial scale factor (how much object-contain scales the image)
  const [initialScaleFactor, setInitialScaleFactor] = useState<number>(1);

  // Ref to the react-zoom-pan-pinch instance
  const transformRef = useRef<ReactZoomPanPinchRef>(null);

  // Ref to the container element for size observation
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Ref to the image element for resize recalculations
  const imageRef = useRef<HTMLImageElement | null>(null);

  // Track the last known zoom level to prevent infinite loops
  const lastZoomLevelRef = useRef<ZoomLevel>('fit');

  // Debounce timer for scale updates
  const scaleUpdateTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Use shared state if available, otherwise use local state
  const effectiveZoomLevel = sharedState?.zoomLevel || localState.zoomLevel;
  const state = {
    ...localState,
    zoomLevel: effectiveZoomLevel,
  };

  /**
   * Get the appropriate image variant based on target dimensions
   * Feature 646: Uses selectBestVariant() for long-side comparison
   * Also considers currentScale from trackpad pinch-zoom gestures
   */
  const getImageSource = useCallback((
    zoomLevel: ZoomLevel,
    scale: number
  ): string => {
    const fallbackUrl = photo.variants?.[0]?.storageUrl || '';

    if (!photo.variants || photo.variants.length === 0) {
      return fallbackUrl;
    }

    // Feature 646: Select best variant based on target dimensions
    // Uses long-side comparison for accurate resolution selection
    let selectedVariant: ReturnType<typeof selectBestVariant>;

    if (targetWidth && targetHeight) {
      selectedVariant = selectBestVariant(photo.variants, targetWidth, targetHeight);
    } else {
      // Fallback to zoom-based selection if dimensions not available
      // If scale > 1.0 (trackpad pinch-zoom), always use full resolution
      if (scale > 1.0) {
        selectedVariant = 'full';
      } else {
        selectedVariant = selectImageResolution(zoomLevel, targetWidth);
      }
    }

    if (!selectedVariant) {
      return fallbackUrl;
    }

    return getVariantUrl(photo, selectedVariant) || fallbackUrl;
  }, [photo, targetWidth, targetHeight]);

  const [imageSource, setImageSource] = useState<string>(() => getImageSource('fit', 1));

  // Track imageSource in a ref to avoid including it in effect dependencies
  const imageSourceRef = useRef<string>(imageSource);

  /**
   * Update image source when photo, zoom level, or actual scale changes
   */
  useEffect(() => {
    const newSource = getImageSource(
      state.zoomLevel,
      currentScale
    );
    if (newSource !== imageSourceRef.current) {
      imageSourceRef.current = newSource;
      setIsLoading(true);
      setImageSource(newSource);
      onImageSourceChange?.(newSource);

      if (sharedState) {
        sharedState.onLoadingChange(true);
      }
    }
  }, [photo.id, getImageSource, state.zoomLevel, currentScale, onImageSourceChange, sharedState]);

  /**
   * Calculate initial scale factor when image loads
   * This tells us how much object-contain scaled the image
   */
  const handleImageLoadForScale = useCallback((img: HTMLImageElement) => {
    if (!img) return;

    // Store image ref for resize recalculations
    imageRef.current = img;

    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    const displayWidth = img.clientWidth;
    const displayHeight = img.clientHeight;

    // Calculate how much object-contain scaled the image
    const scaleX = displayWidth / naturalWidth;
    const scaleY = displayHeight / naturalHeight;
    const scale = Math.min(scaleX, scaleY);

    logger.debug('useZoomPan', 'Image loaded - calculating initialScaleFactor:', {
      naturalWidth,
      naturalHeight,
      displayWidth,
      displayHeight,
      scaleX,
      scaleY,
      initialScaleFactor: scale,
      explanation: `Image displayed at ${(scale * 100).toFixed(1)}% of native size`
    });

    logger.debug('ZoomPan', 'Image loaded:', {
      natural: { width: naturalWidth, height: naturalHeight },
      display: { width: displayWidth, height: displayHeight },
      initialScaleFactor: scale,
      explanation: `At "fit" zoom, image is displayed at ${(scale * 100).toFixed(1)}% of native size`
    });

    setInitialScaleFactor(scale);
    logger.debug('useZoomPan', 'Set initialScaleFactor to:', scale);

    // Clear loading state after image loads
    setIsLoading(false);
    if (sharedState) {
      sharedState.onLoadingChange(false);
    }
  }, [sharedState]);

  /**
   * Calculate the scale factor for a given zoom level.
   * Scale is relative to native image size (100% = 1 image pixel = 1 physical pixel)
   * Accounts for device pixel ratio for true 1:1 pixel mapping
   */
  const calculateScale = useCallback((level: ZoomLevel): number => {
    const dpr = window.devicePixelRatio || 1;

    logger.debug('useZoomPan', 'calculateScale called:', {
      level,
      initialScaleFactor,
      dpr
    });

    let scale: number;

    if (level === 'fit') {
      scale = 1.0;  // Fit to viewport (library's scale 1.0)
    } else if (typeof level === 'number') {
      // DPI-aware zoom calculation
      // At 100% zoom on 2x display: 1 source pixel = 1 physical pixel
      // This requires: CSS pixels = source pixels / DPR

      // scale = (target CSS size) / (current CSS size)
      // target CSS size = (source size * zoomLevel / 100) / DPR
      // current CSS size = source size * initialScaleFactor
      // Therefore: scale = (zoomLevel / 100) / (DPR * initialScaleFactor)
      scale = (level / 100) / (dpr * initialScaleFactor);

      logger.debug('useZoomPan', 'Scale calculation:', {
        formula: `(${level} / 100) / (${dpr} * ${initialScaleFactor})`,
        result: scale
      });
    } else {
      // Fallback for unknown levels
      scale = 1.0;
    }

    logger.debug('ZoomPan', `calculateScale(${level}):`, {
      initialScaleFactor,
      devicePixelRatio: dpr,
      libraryScale: scale,
      nativePercentage: level === 'fit' ? 'fit-to-viewport' : `${level}%`
    });

    return scale;
  }, [initialScaleFactor]);

  /**
   * Sync our state with the library's transform state
   * Uses debouncing to track scale changes without causing spinning
   */
  const handleTransformed = useCallback((ref: ReactZoomPanPinchRef) => {
    // Debounce scale updates to avoid excessive state changes during animations
    // Only update scale after user stops transforming for 150ms
    if (scaleUpdateTimerRef.current) {
      clearTimeout(scaleUpdateTimerRef.current);
    }

    scaleUpdateTimerRef.current = setTimeout(() => {
      const newScale = ref.instance.transformState.scale;
      setCurrentScale(newScale);
    }, 150);
  }, []);

  /**
   * Set zoom level programmatically
   */
  const setZoomLevel = useCallback((level: ZoomLevel) => {
    logger.debug('useZoomPan', 'setZoomLevel called:', level);
    logger.debug('ZoomPan', `setZoomLevel(${level}) called`);

    if (!transformRef.current) {
      logger.warn('useZoomPan', 'transformRef.current is null!');
      logger.warn('ZoomPan', 'transformRef.current is null, cannot set zoom level');
      return;
    }

    const newScale = calculateScale(level);
    logger.debug('useZoomPan', 'Calculated scale:', newScale, 'for level:', level);

    // Just update the library's transform - don't update React state to avoid loops
    if (level === 'fit') {
      logger.debug('useZoomPan', 'Calling resetTransform()');
      logger.debug('ZoomPan', 'Calling resetTransform()');
      transformRef.current.resetTransform();
    } else {
      logger.debug('useZoomPan', 'Calling centerView with scale:', newScale);
      logger.debug('ZoomPan', `Calling centerView(${newScale})`);
      // Center and zoom to the specified scale
      transformRef.current.centerView(newScale);
    }

    // Update local tracking ref
    lastZoomLevelRef.current = level;
    logger.debug('useZoomPan', 'setZoomLevel complete, updated lastZoomLevelRef to:', level);
    logger.debug('ZoomPan', `setZoomLevel(${level}) complete`);
  }, [calculateScale]);

  /**
   * Reset zoom and pan to initial state
   */
  const reset = useCallback(() => {
    logger.debug('useZoomPan', 'reset() called - NOT resetting initialScaleFactor');

    // Reset the ref tracking
    lastZoomLevelRef.current = 'fit';

    // DON'T reset initialScaleFactor here - it should persist across zoom changes
    // Only reset when image loads (in handleImageLoadForScale)
    // setInitialScaleFactor(1); // REMOVED
    setCurrentScale(1);

    setLocalState({
      zoomLevel: 'fit',
      scale: 1,
      panX: 0,
      panY: 0,
      isPanning: false,
    });

    if (transformRef.current) {
      transformRef.current.resetTransform();
    }

    const newSource = getImageSource('fit', 1);
    if (newSource !== imageSourceRef.current) {
      imageSourceRef.current = newSource;
      setIsLoading(true);
      setImageSource(newSource);
      onImageSourceChange?.(newSource);
    }
  }, [getImageSource, onImageSourceChange]);

  /**
   * Get cursor style based on current state
   */
  const getCursorStyle = useCallback((): string => {
    if (state.zoomLevel === 'fit') {
      return 'auto';
    }

    if (state.isPanning) {
      return 'grabbing';
    }

    return 'grab';
  }, [state.zoomLevel, state.isPanning]);

  /**
   * Observe container size changes to enable smart resolution selection
   */
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  /**
   * Handle window resize - recalculate scale factor and reset to fit
   */
  useEffect(() => {
    const handleResize = () => {
      // Recalculate the initial scale factor since object-contain scaling changed
      if (imageRef.current) {
        const img = imageRef.current;
        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;
        const displayWidth = img.clientWidth;
        const displayHeight = img.clientHeight;

        const scaleX = displayWidth / naturalWidth;
        const scaleY = displayHeight / naturalHeight;
        const scale = Math.min(scaleX, scaleY);

        setInitialScaleFactor(scale);
      }

      // Reset to fit on resize (current zoom level might not make sense anymore)
      if (transformRef.current) {
        transformRef.current.resetTransform();
        lastZoomLevelRef.current = 'fit';
      }

      // Check viewport size and show warning if too small
      const width = window.innerWidth;
      if (width < 768 && state.zoomLevel !== 'fit') {
        setViewportWarning('Zoom features work best on larger displays');
      } else if (width < 480) {
        setViewportWarning('This display is very narrow - zoom navigation may be limited');
      } else {
        setViewportWarning(null);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [state.zoomLevel]);

  /**
   * Reset zoom/pan when photo changes
   */
  useEffect(() => {
    reset();
  }, [photo.id, reset]); // Reset when photo ID changes

  /**
   * Feature 646: Zoom is now controlled via explicit CSS dimensions, not TransformWrapper scaling
   * TransformWrapper is only used for panning, so we don't sync zoom levels
   * This useEffect is disabled - zoom changes are handled by DetailViewItem2's displayDimensions
   */
  // useEffect(() => {
  //   ... zoom sync disabled for feature 646
  // }, [effectiveZoomLevel, setZoomLevel]);

  /**
   * Cleanup debounce timer on unmount
   */
  useEffect(() => {
    return () => {
      if (scaleUpdateTimerRef.current) {
        clearTimeout(scaleUpdateTimerRef.current);
      }
    };
  }, []);

  return {
    zoomLevel: state.zoomLevel,
    scale: state.scale,
    isPanning: state.isPanning,
    isLoading,
    imageSource,
    viewportWarning,
    transformRef,
    containerRef,
    containerSize,
    setZoomLevel,
    reset,
    getCursorStyle,
    handleTransformed,
    handleImageLoadForScale,
  };
}
